import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Row,
  Form,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from "react-bootstrap";
import { useMsal } from "@azure/msal-react";
import CancelModal from "./CancelModal";
import Icon from "../generic/Icon";
import "./CarrierForm.css";

const ContactsTable = ({
  contacts = [],
  allFilterData,
  handleSort,
  sortConfig,
  selectedOptions,
  setSelectedOptions,
  handleInputChange,
  handleSaveContact,
  handleCancelEdit,
  handleEdit,
  editId,
  handleAddContact,
  isContactsModified,
  timezoneData,
  languageData,
  cancelConfirm,
  handleDeleteContact,
  setShowDeleteModal,
  setDeleteData,
}) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelData] = useState({});
  const [modalType] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const newContactRef = useRef(null);

  // Get Roles for current user
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const roles = activeAccount?.idTokenClaims.roles || [];
  const isAdmin = roles.includes("cvp.write");

  const rowRefs = useRef({}); // Store refs for each row

  // Scroll the row into view when `editId` changes
  useEffect(() => {
    if (editId && rowRefs.current[editId]) {
      rowRefs.current[editId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [editId]);

  const handleAddContactWithFocus = () => {
    handleAddContact();
    setTimeout(() => {
      if (newContactRef.current) {
        newContactRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        newContactRef.current.focus();
      }
    }, 100); // Delay to ensure the DOM is updated
  };

  return (
    <div
      style={{
        border: "2px solid #dee2e6",
        borderRadius: "8px",
        padding: "20px",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <h6>Contacts</h6>
        <>
          <div
            style={{
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: "50%",
              width: "1.5rem",
              height: "1.5rem",
              marginBottom: "10px",
            }}
          >
            {isAdmin && (
              <>
                {editId === null ? (
                  <Icon
                    name="add"
                    onClick={isAdmin ? handleAddContactWithFocus : null}
                    overrides={{
                      tooltip: !isAdmin
                        ? "You need admin privileges to edit"
                        : "Add Contacts",
                      tooltipPlacement: "auto",
                      disabled: !isAdmin,
                      size: "1.5rem",
                      backgroundColor: "transparent",
                    }}
                    id="add-contact-icon"
                  />
                ) : (
                  <Icon
                    name="add"
                    overrides={{
                      tooltip: editId
                        ? `Editing mode active for contact: ${
                            contacts.find((contact) => contact.id === editId)
                              ?.name || "Unknown"
                          }. Please finish editing.`
                        : "No contact is being edited.",
                      tooltipPlacement: "auto",
                      disabled: true,
                      size: "1.5rem",
                      style: {
                        color: "gray",
                      },
                    }}
                    id="add-contact-disabled-icon"
                  />
                )}
              </>
            )}
          </div>
        </>
      </div>

      <Row style={{ marginBottom: "10px" }}>
        <div className="contacts-table-container">
          <Table striped bordered className="contacts-table">
            <thead>
              <tr>
                <th>Name *</th>
                <th>
                  <div>
                    <OverlayTrigger
                      placement="auto"
                      overlay={
                        <Tooltip id="role-tooltip">
                          Click to sort by Role
                        </Tooltip>
                      }
                    >
                      <SortIcon
                        sortConfig={sortConfig}
                        columnKey="role"
                        headerText="Role *"
                        onClick={() => handleSort("role")}
                      />
                    </OverlayTrigger>
                    <MyDropDown
                      isDisabled={false}
                      title={"Role"}
                      filterData={allFilterData?.Role || []}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div>
                    <span>Type</span>
                    <MyDropDown
                      isDisabled={false}
                      title={"Type"}
                      filterData={[
                        ...new Set(
                          contacts
                            .map((contact) => contact.type)
                            .filter(Boolean)
                        ),
                      ].map((type) => ({ name: type }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div>
                    <span>Region</span>
                    <MyDropDown
                      isDisabled={false}
                      title={"Region"}
                      filterData={[
                        ...new Set(
                          contacts
                            .map((contact) => contact.region)
                            .filter(Boolean)
                        ),
                      ].map((region) => ({ name: region }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div>
                    <span>Timezone</span>
                    <MyDropDown
                      isDisabled={false}
                      title={"Timezone"}
                      filterData={[
                        ...new Set(
                          contacts
                            .map((contact) => contact.timezoneCode)
                            .filter(Boolean)
                        ),
                      ].map((timezone) => ({ name: timezone }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>
                  <div>
                    <span>Language</span>
                    <MyDropDown
                      isDisabled={false}
                      title={"Language"}
                      filterData={[
                        ...new Set(
                          contacts
                            .map((contact) => contact.language)
                            .filter(Boolean)
                        ),
                      ].map((language) => ({ name: language }))}
                      selectedOptions={selectedOptions}
                      setSelectedOptions={setSelectedOptions}
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                </th>
                <th>Email</th>
                <th>Phone 1</th>
                <th>Phone 2</th>
                <th>Notes</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className={contact.id === editId ? "editing-row" : ""}
                  ref={(row) => (rowRefs.current[contact.id] = row)} // Assign ref to each row
                >
                  {contact.id === editId ? (
                    <>
                      <td>
                        <Form.Control
                          type="text"
                          name="name"
                          value={contact.name}
                          onChange={(e) => handleInputChange(e)}
                        />
                      </td>
                      <td>
                        <Form.Select
                          name="role"
                          value={contact.role}
                          onChange={(e) => handleInputChange(e)}
                        >
                          <option value="" disabled>
                            Select Role
                          </option>
                          <option value="Support">Support</option>
                          <option value="Escalation">Escalation</option>
                          <option value="Account">Account</option>
                          <option value="Billing">Billing</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          name="type"
                          value={contact.type}
                          onChange={(e) => handleInputChange(e)}
                        >
                          <option value="" disabled>
                            Select Type
                          </option>
                          <option value="Admin">Admin</option>
                          <option value="Data">Data</option>
                          <option value="General">General</option>
                          <option value="Voice">Voice</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          name="region"
                          value={contact.region}
                          onChange={(e) => handleInputChange(e)}
                        >
                          <option value="" disabled>
                            Select Region
                          </option>
                          <option value="GME">GME</option>
                          <option value="GMIO">GMIO</option>
                          <option value="GMNA">GMNA</option>
                          <option value="GMSA">GMSA</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          name="timezoneCode"
                          value={
                            contact.timezoneCode ? contact.timezoneCode : ""
                          }
                          onChange={(e) => handleInputChange(e)}
                        >
                          <option value="" disabled>
                            Select Timezone
                          </option>
                          {timezoneData.map((timezone, index) => (
                            <option key={index} value={timezone} disabled={timezone === "Most Common" || timezone === "All Timezones"} style={timezone === "All Timezones" || timezone === "Most Common" ? { fontWeight: "600", backgroundColor: "#d3ceceff", color: "#1b1b1bff" } : {}}>
                              {timezone}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          name="language"
                          value={contact.language ? contact.language : ""}
                          onChange={(e) => handleInputChange(e)}
                        >
                          <option value="" disabled>
                            Select Language
                          </option>
                          {languageData.map((language, index) => (
                            <option key={index} value={language} disabled={language === "Most Common" || language === "All Languages"} style={language === "All Languages" || language === "Most Common" ? { fontWeight: "600", backgroundColor: "#d3ceceff", color: "#1b1b1bff" } : {}}>
                              {language}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          name="email"
                          value={contact.email}
                          onChange={(e) => handleInputChange(e)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          name="mainPhone"
                          value={contact.mainPhone}
                          onChange={(e) => handleInputChange(e)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          name="altPhone"
                          value={contact.altPhone}
                          onChange={(e) => handleInputChange(e)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          as="textarea"
                          name="notes"
                          value={contact.notes}
                          onChange={(e) => handleInputChange(e)}
                          maxLength="1024"
                          style={{
                            height: "70px",
                            overflowY: "auto",
                            resize: "none",
                          }}
                        />
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "5px",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: "white",
                              borderRadius: "50%",
                              width: "1.2rem",
                              height: "1.2rem",
                              marginRight: "20px",
                              cursor: "pointer",
                            }}
                          >
                            <Icon
                              name="discard"
                              onClick={handleCancelEdit}
                              overrides={{
                                tooltip: "Discard Contact Changes",
                                tooltipPlacement: "auto",
                              }}
                              id={`cancel-contact-icon-${contact.id}`}
                            />
                          </div>
                          <Icon
                            name="save"
                            onClick={handleSaveContact}
                            overrides={{
                              tooltip: isContactsModified
                                ? "Save Contact"
                                : "No changes to save",
                              tooltipPlacement: "auto",
                              disabled: !isContactsModified,
                              style: {
                                marginRight: "20px",
                                cursor: "pointer",
                              },
                            }}
                            id={`save-contact-icon-${contact.id}`}
                          />
                          <Icon
                            name="delete"
                            onClick={
                              contact.id && contact.id !== -1
                                ? () => {
                                    setShowDeleteModal(true);
                                    setDeleteData(contact);
                                  }
                                : null
                            }
                            overrides={{
                              tooltip: "Delete Contact",
                              tooltipPlacement: "auto",
                              disabled: !(contact.id && contact.id !== -1),
                              style: {
                                cursor: "pointer",
                              },
                            }}
                            id={`delete-contact-icon-${contact.id}`}
                          />
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{contact.name}</td>
                      <td>{contact.role}</td>
                      <td>{contact.type}</td>
                      <td>{contact.region}</td>
                      <td>{contact.timezoneCode}</td>
                      <td>{contact.language}</td>
                      <td>{contact.email}</td>
                      <td>{contact.mainPhone}</td>
                      <td>{contact.altPhone}</td>
                      <td>
                        <OverlayTrigger
                          placement="auto"
                          offset={[0, 0.5]}
                          overlay={
                            <Tooltip id={`tooltip-${contact.id}`}>
                              {contact.notes || "N/A"}
                            </Tooltip>
                          }
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "100%",
                              height: "20px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {contact.notes}
                          </span>
                        </OverlayTrigger>
                      </td>
                      {isAdmin && (
                        <>
                          <td>
                            {editId === null ? (
                              <Icon
                                name="edit"
                                onClick={() =>
                                  isAdmin && handleEdit(contact.id)
                                }
                                overrides={{
                                  tooltip: !isAdmin
                                    ? "You need admin privileges to edit"
                                    : "Edit Contact",
                                  tooltipPlacement: "auto",
                                  disabled: !isAdmin,
                                  size: "1.0rem",
                                  style: {
                                    cursor: "pointer",
                                  },
                                }}
                                id={`edit-contact-icon-${contact.id}`}
                              />
                            ) : (
                              <Icon
                                name="edit"
                                overrides={{
                                  tooltip: editId
                                    ? `Editing mode active for contact: ${
                                        contacts.find(
                                          (contact) => contact.id === editId
                                        )?.name || "Unknown"
                                      }. Please finish editing.`
                                    : "No contact is being edited.",
                                  tooltipPlacement: "auto",
                                  disabled: true,
                                  size: "1.0rem",
                                  style: {
                                    color: "gray",
                                  },
                                }}
                                id={`edit-contact-disabled-icon-${contact.id}`}
                              />
                            )}
                          </td>
                        </>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {editId && contacts.find((contact) => contact.id === editId) && (
          <CancelModal
            type={modalType}
            show={showCancelModal}
            setShow={setShowCancelModal}
            data={cancelData}
            cancelConfirm={cancelConfirm}
            handleDeleteContact={handleDeleteContact}
          />
        )}
      </Row>
    </div>
  );
};

const SortIcon = React.forwardRef(
  ({ sortConfig, columnKey, headerText, onClick, ...props }, ref) => {
    return (
      <span
        ref={ref}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          cursor: "pointer",
          minWidth: "60px",
        }}
        onClick={onClick}
        {...props}
      >
        {headerText}
        {sortConfig.key === columnKey && sortConfig.direction && (
          <Icon
            name={sortConfig.direction === "asc" ? "caretUp" : "caretDown"}
            overrides={{
              tooltip: `Sort ${
                sortConfig.direction === "asc" ? "Ascending" : "Descending"
              }`,
              tooltipPlacement: "top",
              style: {
                fontSize: "1rem",
                cursor: "pointer",
              },
            }}
            id={`sort-icon-${columnKey}`}
          />
        )}
      </span>
    );
  }
);

const MyDropDown = ({
  title,
  filterData = [],
  selectedOptions,
  setSelectedOptions,
  openDropdown,
  setOpenDropdown,
}) => {
  const [dropDownFilter, setDropDownFilter] = useState("");
  const isOpen = openDropdown === title;

  const displayedData = (filterData || []).filter((item) =>
    item.name?.toLowerCase().includes(dropDownFilter.toLowerCase())
  );

  const isFilterActive = selectedOptions[title]?.length > 0;

  const handleCheckboxChange = (key, item) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [key]: prev[key]?.includes(item)
        ? prev[key].filter((i) => i !== item)
        : [...(prev[key] || []), item],
    }));
  };

  const handleClearFilters = () => {
    setSelectedOptions((prev) => ({
      ...prev,
      [title]: [],
    }));
  };

  return (
    <Dropdown
      align="end"
      show={isOpen}
      onToggle={(isOpen) => {
        if (isOpen) {
          setOpenDropdown(title);
        } else {
          setOpenDropdown(null);
        }
      }}
    >
      <Dropdown.Toggle
        className="no-caret"
        as="span"
        style={{
          cursor: "pointer",
          marginLeft: "5px",
          fontSize: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isFilterActive) {
            // Clear filters if active
            handleClearFilters();
          } else {
            // Toggle dropdown if no filters are active
            setOpenDropdown(isOpen ? null : title);
          }
        }}
      >
        <Icon
          name={isFilterActive ? "removeFilter" : "filter"}
          overrides={{
            tooltip: isFilterActive ? "Clear filters" : "Filter table",
            tooltipPlacement: "top",
            iconColor: "#FFFFFF",
            style: {
              cursor: "pointer",
            },
          }}
          id={`filter-dropdown-${title}`}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-menu w-100 p-2"
        style={{
          maxHeight: "250px",
          overflow: "auto",
          zIndex: 1050,
        }}
      >
        <Form.Control
          type="text"
          placeholder="Filter..."
          value={dropDownFilter}
          onChange={(e) => setDropDownFilter(e.target.value)}
        />
        <hr className="my-1" />

        <Form.Check
          type="checkbox"
          label="Select All"
          checked={selectedOptions[title]?.length === filterData?.length}
          onChange={() => {
            setSelectedOptions((prev) => ({
              ...prev,
              [title]:
                prev[title]?.length === filterData?.length
                  ? []
                  : (filterData || []).map((s) => s.name),
            }));
          }}
          className="px-3 ms-1 fw-bold"
        />
        <hr className="my-1" />

        {displayedData.map((item, index) => (
          <Form.Check
            key={index}
            type="checkbox"
            label={item.name}
            checked={selectedOptions[title]?.includes(item.name) || false}
            onChange={() => handleCheckboxChange(title, item.name)}
            className="px-3 ms-1"
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ContactsTable;
